import { MiniKit } from '@worldcoin/minikit-js';
import { t } from 'i18next';
import hold353Abi from '@/abi/hold353.json';
import { CONSTANT } from '@/store/constants';
import { waitForTransactionReceipt } from '@/utils/wait-transaction';

// HOLD-353 open-trade lucky draw (World Chain). Backend rolls one reward per
// executed order and signs it; the user's account then calls
// Storefront.claimHold353.
export const HOLD353_API = import.meta.env.VITE_HOLD353_API;

export type Hold353Claim = {
    order_id: string;
    /** 1 = 10 AION, 0 = better luck next time */
    reward: 0 | 1;
    deadline: string;
    signature: string;
};

export type Hold353Response = {
    user: string;
    tx_hash: string;
    block_number: string;
    claims: Hold353Claim[];
};

// Friendly, translated message per final API status. The server's own
// `{"error": string}` is only logged. Literal t() calls keep `node check.js` aware of the keys.
function getStatusMessage(status: number) {
    switch (status) {
        case 409:
            return t('You have already claimed the lucky draw for this trade');
        case 422:
            return t('No lucky draw available for this trade');
        case 503:
            return t('Lucky draw rewards have run out');
        default:
            return t('Something went wrong, please try again');
    }
}

// 404 tx not indexed, 425 order not executed yet (fills 1-2 blocks after the
// open), 502 backend RPC error. Anything else is final (400/409/422/503).
const RETRYABLE = new Set([404, 425, 502]);
const MAX_RETRIES = 20;
const RETRY_DELAY_MS = 3_000;

export async function fetchHold353Claims(
    user: string,
    txHash: string
): Promise<Hold353Response> {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const res = await fetch(HOLD353_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user, tx_hash: txHash }),
        });
        if (res.ok) {
            const data = (await res.json()) as Hold353Response;
            if (!data.claims?.length) {
                throw new Error(t('No lucky draw available for this trade'));
            }
            return data;
        }
        const body = await res.json().catch(() => ({ error: res.statusText }));
        if (!RETRYABLE.has(res.status)) {
            console.error('HOLD-353 API', res.status, body?.error);
            throw new Error(getStatusMessage(res.status));
        }
        if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        }
    }
    throw new Error(t('Lucky draw is not ready yet, please try again later'));
}

/** Sends Storefront.claimHold353 for every claim via MiniKit and waits for the receipt. */
export async function sendHold353Claims(resp: Hold353Response) {
    const { finalPayload } = await MiniKit.commandsAsync.sendTransaction({
        transaction: resp.claims.map((c) => ({
            address: CONSTANT.CONTRACT_STOREFRONT,
            abi: hold353Abi,
            functionName: 'claimHold353',
            args: [
                c.order_id,
                resp.block_number,
                c.reward,
                c.deadline,
                c.signature,
            ],
        })),
    });
    if (finalPayload.status === 'error') {
        throw new Error(finalPayload.error_code);
    }
    await waitForTransactionReceipt(finalPayload.transaction_id);
}
